// ============================================================================
// @orgazone/auth-web/client — CÔTÉ NAVIGATEUR du socle d'auth : résolution de session au boot du SPA.
//
// ⚠️ Ce fichier est le PENDANT de `plugin.ts` : le serveur publie le mode, le SPA le lit. Les deux côtés
// d'un contrat vivent dans le même package (même patron que @orgazone/api-envelope : `.` pur / `./fastify`
// / `./client`). Import PUR : rien de Node ici — un SPA qui importe `@orgazone/auth-web/client` ne tire ni
// Fastify ni argon2.
//
// CE QU'IL CORRIGE (audit 2026-08-02) : les trois SPA résolvaient leur session avec
//   try { me() } catch { user = null }
// ce qui confond DEUX réalités opposées — « pas de session » (401, normal : on va au login) et « je n'ai
// pas pu savoir » (service injoignable, 500, réponse illisible). Dans le second cas l'écran affichait
// « non connecté » et renvoyait vers un formulaire de connexion adossé à un service en panne : l'écran
// MENTAIT (§23.0, échec avalé) et l'utilisateur retapait ses identifiants pour rien.
// ============================================================================

import { AUTH_MODE_UNKNOWN, normalizeAuthMode, type AuthMode } from './mode';

/** Session résolue au boot. `error` non nul = état INDÉTERMINÉ à afficher tel quel, pas un « déconnecté ». */
export interface SessionSnapshot<U> {
  mode: AuthMode;
  /** Utilisateur connecté, ou `null` (non connecté, mode ouvert, ou indéterminé — voir `error`). */
  user: U | null;
  /** Message RÉEL de la panne quand la session n'a pas pu être établie ; `null` en fonctionnement normal. */
  error: string | null;
}

export interface ResolveSessionIo<U> {
  /** GET /auth/mode. */
  fetchMode: () => Promise<AuthMode | unknown>;
  /** GET /auth/me — doit LEVER si l'appel échoue (y compris 401) ; c'est `isUnauthenticated` qui trie. */
  fetchUser: () => Promise<U | null>;
  /**
   * Distingue « pas de session » d'une panne. Défaut : `status === 401`, ce que porte `ApiError`
   * (@orgazone/api-envelope) — lu en canard pour ne pas créer de dépendance entre les deux contrats.
   */
  isUnauthenticated?: (e: unknown) => boolean;
}

const defaultIsUnauthenticated = (e: unknown): boolean => (e as { status?: number } | null)?.status === 401;

const messageOf = (e: unknown, fallback: string): string => {
  const m = (e as { message?: string } | null)?.message;
  return m && m.trim() ? m : fallback;
};

/**
 * Résout la session au démarrage du SPA : mode d'auth, puis utilisateur si l'auth est armée.
 * RÈGLE UNIQUE, identique dans toutes les apps :
 *   1. mode illisible          → fail-closed + message réel (jamais « ouvert » : ce serait la boucle)
 *   2. mode non armé           → mode ouvert, pas de session à chercher
 *   3. armé, 401 sur /auth/me  → pas connecté (cas NORMAL, pas une erreur) → login
 *   4. armé, autre échec       → indéterminé : on le DIT au lieu de prétendre une déconnexion
 */
export async function resolveSession<U>(io: ResolveSessionIo<U>): Promise<SessionSnapshot<U>> {
  const isUnauth = io.isUnauthenticated ?? defaultIsUnauthenticated;

  let mode: AuthMode;
  try {
    mode = normalizeAuthMode(await io.fetchMode());
  } catch (e) {
    return { mode: AUTH_MODE_UNKNOWN, user: null, error: messageOf(e, "Service d'authentification injoignable.") };
  }

  if (!mode.armed) return { mode, user: null, error: null };

  try {
    return { mode, user: await io.fetchUser(), error: null };
  } catch (e) {
    if (isUnauth(e)) return { mode, user: null, error: null };
    return { mode, user: null, error: messageOf(e, 'Session indéterminée : impossible de joindre le service.') };
  }
}

export { AUTH_MODE_UNKNOWN, normalizeAuthMode, type AuthMode } from './mode';
